import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { FC, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import BoxLoading from "./BoxLoading";
import moment from "moment";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface BarChartProps {
  data: ChartData<"bar", number[], string>;
  options?: ChartOptions<"bar">;
  title?: string;
  loading?: boolean;
  onBarClick?: (label: string) => void;
  selectedMonth?: string;
}

const BarChart: FC<BarChartProps> = ({
  data,
  options,
  loading = false,
  onBarClick,
  selectedMonth,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  useEffect(() => {
    const timeout = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 100);

    return () => clearTimeout(timeout);
  }, []);

  const chartOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const rawLabel = tooltipItems[0].label;
            return moment(rawLabel, "YYYY-MM").format("MMMM [de] YYYY");
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          callback: function (_, index: number) {
            const rawLabel = this.getLabelForValue(index);
            return moment(rawLabel, "YYYY-MM").format("MMMM/YY");
          },
        },
      },
      y: {
        ticks: {
          callback: function (value: any) {
            return `R$ ${value.toLocaleString("pt-br")}`;
          },
        },
      },
    },
    ...options,
  };

  return (
    <BoxLoading loading={loading}>
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: 250,
          borderRadius: 2,
          px: 2,
          py: 2,
          backgroundColor: "var(--mui-palette-primary-light)",
          border: "1px solid var(--mui-palette-tertiary-light)",
        }}
      >
        <Bar
          ref={chartRef}
          data={data}
          options={chartOptions}
          onClick={(event) => {
            if (!chartRef.current) return;

            const chart = chartRef.current;
            const elements = chart.getElementsAtEventForMode(
              event,
              "nearest",
              { intersect: true },
              false
            );

            if (elements.length > 0 && onBarClick) {
              const index = elements[0].index;
              const label = data.labels?.[index];

              if (typeof label === "string" && label !== selectedMonth) {
                onBarClick(label);
              }
            }
          }}
        />
      </Box>
    </BoxLoading>
  );
};

export default BarChart;
